import { signal, effect } from '@preact/signals';
import { secureStorage } from '../storage/secureStorage.js';

/**
 * Recently visited chapters across all bibles. Cap at 30 entries; newest
 * first. Stored in localStorage and surfaced as a "Recent" section in
 * the ChapterPicker plus an optional "Continue where you left off" pill
 * if the last visit was more than 1 hour ago.
 */

export type HistoryEntry = {
  bookId: string;
  chapter: number;
  bibleId: string;
  visitedAt: number;
};

const STORAGE_KEY = '_h1';
const MAX_ENTRIES = 30;

function load(): HistoryEntry[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export const history = signal<HistoryEntry[]>(load());

if (typeof window !== 'undefined') {
  effect(() => {
    void secureStorage.setItem(STORAGE_KEY, JSON.stringify(history.value));
  });
}

export function visitChapter(bookId: string, chapter: number, bibleId: string): void {
  const upperBook = bookId.toUpperCase();
  const upperBible = bibleId.toUpperCase();
  const list = history.peek();
  const head = list[0];
  // De-dupe back-to-back visits.
  if (
    head &&
    head.bookId === upperBook &&
    head.chapter === chapter &&
    head.bibleId === upperBible
  ) {
    return;
  }
  const next: HistoryEntry = {
    bookId: upperBook,
    chapter,
    bibleId: upperBible,
    visitedAt: Date.now(),
  };
  const filtered = list.filter(
    (e) => !(e.bookId === upperBook && e.chapter === chapter && e.bibleId === upperBible),
  );
  history.value = [next, ...filtered].slice(0, MAX_ENTRIES);
}

export function lastVisited(): HistoryEntry | null {
  return history.value[0] ?? null;
}
